'use client';

import { useIsPreviewing } from '@builder.io/react';
import Image from 'next/image';
import Link from 'next/link';

import dateFromTimestamp from '../../../utils/get-date-from-timestamp';
import styles from './blog-hero.module.css';
import type { Author, BlogHeroProps } from './blog-hero.types';

const DEFAULT_PORTRAIT =
  'https://cdn.builder.io/api/v1/image/assets%2F3124f13d16c344a781aa2e5bdbc00183%2Feb3745a4db0044d5a9abc7f0c3a5589b';

function getDummyData() {
  return {
    category: 'Healthcare News',
    title: 'Sample Article Title - Healthcare Industry Insights',
    description:
      'This is a sample article description that provides a brief overview of the content. It demonstrates how the blog hero component appears with fallback data.',
    author: {
      name: 'Healthcare.com Team',
      portrait: DEFAULT_PORTRAIT,
    },
    publishDate: new Date().toISOString(),
    readTime: 5,
    backUrl: '/partner-news',
  };
}

const getAuthorName = (author?: Author): string =>
  author?.value?.data?.name ?? author?.name ?? getDummyData().author.name;

const getAuthorPortrait = (author?: Author): string => {
  const portrait = author?.value?.data?.portrait ?? author?.portrait;
  return portrait && portrait.trim() !== ''
    ? portrait
    : getDummyData().author.portrait;
};

const joinWithSeparator = (parts: string[]): string => parts.join(' · ');

const formatMetadata = (
  publishDate?: string | number | Date,
  readTime?: number,
): string => {
  const parts: string[] = [];
  if (publishDate) parts.push(dateFromTimestamp(publishDate));
  if (readTime) parts.push(`${readTime} min read time`);
  return joinWithSeparator(parts);
};

const BackButton = ({ backUrl }: { backUrl: string }) => {
  const backIconUrl =
    'https://cdn.builder.io/api/v1/image/assets/3124f13d16c344a781aa2e5bdbc00183/fd6f21e156cc4db0b9582c9c739f74cb';

  return (
    <div className={styles.backContainer}>
      <Link href={backUrl} className={styles.backLink}>
        <Image
          src={backIconUrl}
          className={styles.backIcon}
          alt="Back arrow"
          width={24}
          height={24}
          unoptimized
        />
        <div className={styles.backText}>
          <p>Back</p>
        </div>
      </Link>
    </div>
  );
};

const BlogMetadata = ({
  category,
  publishDate,
  readTime,
}: Pick<BlogHeroProps, 'category' | 'publishDate' | 'readTime'>) => (
  <>
    {category && (
      <div className={styles.categoryText}>
        <p>{category}</p>
      </div>
    )}
    {(publishDate || readTime) && (
      <div className={styles.metadataText}>
        <p>{formatMetadata(publishDate, readTime)}</p>
      </div>
    )}
  </>
);

const BlogContent = ({
  title,
  description,
}: Pick<BlogHeroProps, 'title' | 'description'>) => (
  <>
    {title && (
      <div className={styles.titleText}>
        <p>{title}</p>
      </div>
    )}
    {description && (
      <div className={styles.descriptionText}>
        <p>{description}</p>
      </div>
    )}
  </>
);

const AuthorInfo = ({ author }: Pick<BlogHeroProps, 'author'>) => {
  const name = getAuthorName(author);
  const portrait = getAuthorPortrait(author);

  return (
    <div className={styles.authorContainer}>
      <Image
        src={portrait}
        className={styles.authorImage}
        alt={`Portrait of ${name}`}
        width={48}
        height={48}
        unoptimized
      />
      <div className={styles.authorInfo}>
        <div className={styles.authorName}>
          <p>{name}</p>
        </div>
      </div>
    </div>
  );
};

// Helper function to get alt text - separated for testability
const getAltText = (title?: string): string => {
  return title || 'Article hero image';
};

const HeroImage = ({
  heroImage,
  title,
}: Pick<BlogHeroProps, 'heroImage' | 'title'>) => {
  if (!heroImage || heroImage.trim() === '') return null;

  const altText = getAltText(title);

  return (
    <Image
      src={heroImage}
      className={styles.heroImage}
      alt={altText}
      width={600}
      height={400}
      priority
    />
  );
};

// Helper function to get safe description - separated for testability
const getSafeDescription = (
  description?: string,
  isPreviewing?: boolean,
): string => {
  if (description) return description;
  return isPreviewing ? getDummyData().description : 'Article description...';
};

function BlogHero({
  category,
  publishDate,
  readTime,
  title,
  description,
  author,
  heroImage,
  backUrl = '/partner-news',
}: BlogHeroProps) {
  const isPreviewing = useIsPreviewing();

  // Simplified logic for 100% coverage - use dummy data only in preview mode
  const hasImage = heroImage && heroImage.trim() !== '';
  const DUMMY_DATA = getDummyData();

  const safeCategory =
    category || (isPreviewing ? DUMMY_DATA.category : 'Healthcare News');
  const safeTitle =
    title?.trim() || (isPreviewing ? DUMMY_DATA.title : 'Article Title');
  const safeDescription = getSafeDescription(description, isPreviewing);
  const safeAuthor = author || DUMMY_DATA.author;
  const safePublishDate = publishDate || DUMMY_DATA.publishDate;
  const safeReadTime = readTime || DUMMY_DATA.readTime;
  const safeBackUrl = backUrl || DUMMY_DATA.backUrl;

  return (
    <div className={styles.container}>
      <section className={styles.section}>
        <BackButton backUrl={safeBackUrl} />

        <div className={hasImage ? styles.mainGrid : styles.centeredContent}>
          <div
            className={hasImage ? styles.contentColumn : styles.centeredColumn}
          >
            <BlogMetadata
              category={safeCategory}
              publishDate={safePublishDate}
              readTime={safeReadTime}
            />

            <BlogContent title={safeTitle} description={safeDescription} />

            <AuthorInfo author={safeAuthor} />
          </div>

          {hasImage && <HeroImage heroImage={heroImage} title={safeTitle} />}
        </div>
      </section>
    </div>
  );
}

export default BlogHero;

// Export internal components and helper functions for testing purposes
export {
  AuthorInfo,
  BlogContent,
  BlogMetadata,
  getAltText,
  getSafeDescription,
  HeroImage,
};
